import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface AdminReview {
  id: string;
  productId: number;
  productName: string;
  reviewerName: string;
  reviewerPhone: string;
  rating: number;
  reviewText: string;
  isVerified: boolean;
  isNew: boolean;
  createdAt: string;
  adminReply?: string;
  adminReplyAt?: string;
}

interface Props {
  onToast: (msg: string, type?: "success" | "error") => void;
  onNewReviewCount?: (count: number) => void;
}

function StarDisplay({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`ri-star-fill text-sm ${s <= rating ? "text-amber-400" : "text-gray-200"}`}
        ></i>
      ))}
    </div>
  );
}

const RATING_LABELS: Record<number, { label: string; color: string }> = {
  5: { label: "ممتاز",    color: "text-green-600 bg-green-50 border-green-200" },
  4: { label: "جيد جداً", color: "text-teal-600 bg-teal-50 border-teal-200" },
  3: { label: "جيد",      color: "text-amber-600 bg-amber-50 border-amber-200" },
  2: { label: "مقبول",    color: "text-orange-600 bg-orange-50 border-orange-200" },
  1: { label: "سيء",      color: "text-red-600 bg-red-50 border-red-200" },
};

export default function ReviewsManager({ onToast, onNewReviewCount }: Props) {
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRating, setFilterRating] = useState<number | "all">("all");
  const [filterVerified, setFilterVerified] = useState<"all" | "verified" | "unverified">("all");
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());

  // Reply state: id → draft text
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  // Which review has the reply box open
  const [replyOpen, setReplyOpen] = useState<string | null>(null);
  const [savingReply, setSavingReply] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("admin_seen_review_ids");
      if (stored) setSeenIds(new Set(JSON.parse(stored)));
    } catch { /* ignore */ }
  }, []);

  const markAllSeen = useCallback((ids: string[]) => {
    const newSet = new Set([...Array.from(seenIds), ...ids]);
    setSeenIds(newSet);
    try {
      localStorage.setItem("admin_seen_review_ids", JSON.stringify(Array.from(newSet)));
    } catch { /* ignore */ }
  }, [seenIds]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const { data: reviewsData, error } = await supabase
        .from("reviews")
        .select("*")
        .order("created_at", { ascending: false });

      if (error || !reviewsData) { setLoading(false); return; }

      const productIds = [...new Set(reviewsData.map((r) => r.product_id as number))];
      let productMap: Record<number, string> = {};
      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from("products")
          .select("id, name_ar")
          .in("id", productIds);
        if (products) {
          products.forEach((p: { id: number; name_ar: string }) => {
            productMap[p.id] = p.name_ar;
          });
        }
      }

      const mapped: AdminReview[] = reviewsData.map((r) => ({
        id: String(r.id),
        productId: Number(r.product_id),
        productName: productMap[Number(r.product_id)] || `منتج #${r.product_id}`,
        reviewerName: String(r.reviewer_name ?? ""),
        reviewerPhone: String(r.reviewer_phone ?? ""),
        rating: Number(r.rating ?? 5),
        reviewText: String(r.review_text ?? ""),
        isVerified: Boolean(r.is_verified),
        isNew: !seenIds.has(String(r.id)),
        createdAt: String(r.created_at ?? ""),
        adminReply: r.admin_reply ? String(r.admin_reply) : undefined,
        adminReplyAt: r.admin_reply_at ? String(r.admin_reply_at) : undefined,
      }));

      setReviews(mapped);
      const newCount = mapped.filter((r) => r.isNew).length;
      onNewReviewCount?.(newCount);
    } finally {
      setLoading(false);
    }
  }, [seenIds, onNewReviewCount]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("admin-reviews-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "reviews" }, (payload) => {
        const r = payload.new as Record<string, unknown>;
        const newReview: AdminReview = {
          id: String(r.id),
          productId: Number(r.product_id),
          productName: `منتج #${r.product_id}`,
          reviewerName: String(r.reviewer_name ?? ""),
          reviewerPhone: String(r.reviewer_phone ?? ""),
          rating: Number(r.rating ?? 5),
          reviewText: String(r.review_text ?? ""),
          isVerified: Boolean(r.is_verified),
          isNew: true,
          createdAt: String(r.created_at ?? ""),
        };
        setReviews((prev) => [newReview, ...prev]);
        onNewReviewCount?.((prev) => prev + 1);
        supabase.from("products").select("name_ar").eq("id", Number(r.product_id)).maybeSingle().then(({ data }) => {
          if (data?.name_ar) {
            setReviews((prev) => prev.map((rev) => rev.id === String(r.id) ? { ...rev, productName: data.name_ar } : rev));
          }
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [onNewReviewCount]);

  const handleMarkSeen = useCallback(() => {
    const allIds = reviews.map((r) => r.id);
    markAllSeen(allIds);
    setReviews((prev) => prev.map((r) => ({ ...r, isNew: false })));
    onNewReviewCount?.(0);
  }, [reviews, markAllSeen, onNewReviewCount]);

  const handleDelete = useCallback(async (id: string, name: string) => {
    if (!window.confirm(`حذف تقييم "${name}"؟`)) return;
    setDeleting(id);
    const { error } = await supabase.from("reviews").delete().eq("id", id);
    if (!error) {
      setReviews((prev) => prev.filter((r) => r.id !== id));
      onToast("تم حذف التقييم ✓");
      const newCount = reviews.filter((r) => r.id !== id && r.isNew).length;
      onNewReviewCount?.(newCount);
    } else {
      onToast("حدث خطأ أثناء الحذف", "error");
    }
    setDeleting(null);
  }, [reviews, onToast, onNewReviewCount]);

  const handleSaveReply = useCallback(async (reviewId: string) => {
    const draft = replyDrafts[reviewId]?.trim();
    if (!draft) {
      onToast("يرجى كتابة رد أولاً", "error");
      return;
    }
    setSavingReply(reviewId);
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("reviews")
      .update({ admin_reply: draft, admin_reply_at: now })
      .eq("id", reviewId);

    if (!error) {
      setReviews((prev) =>
        prev.map((r) =>
          r.id === reviewId ? { ...r, adminReply: draft, adminReplyAt: now } : r
        )
      );
      setReplyOpen(null);
      onToast("تم حفظ الرد وسيظهر على صفحة المنتج ✓");
    } else {
      onToast("حدث خطأ أثناء حفظ الرد", "error");
    }
    setSavingReply(null);
  }, [replyDrafts, onToast]);

  const handleDeleteReply = useCallback(async (reviewId: string) => {
    setSavingReply(reviewId);
    const { error } = await supabase
      .from("reviews")
      .update({ admin_reply: null, admin_reply_at: null })
      .eq("id", reviewId);

    if (!error) {
      setReviews((prev) =>
        prev.map((r) =>
          r.id === reviewId ? { ...r, adminReply: undefined, adminReplyAt: undefined } : r
        )
      );
      setReplyDrafts((prev) => { const n = { ...prev }; delete n[reviewId]; return n; });
      setReplyOpen(null);
      onToast("تم حذف الرد ✓");
    } else {
      onToast("حدث خطأ أثناء حذف الرد", "error");
    }
    setSavingReply(null);
  }, [onToast]);

  const filtered = reviews.filter((r) => {
    const matchRating = filterRating === "all" || r.rating === filterRating;
    const matchVerified =
      filterVerified === "all" ||
      (filterVerified === "verified" && r.isVerified) ||
      (filterVerified === "unverified" && !r.isVerified);
    const matchSearch =
      !search ||
      r.reviewerName.includes(search) ||
      r.reviewerPhone.includes(search) ||
      r.productName.includes(search) ||
      r.reviewText.includes(search);
    return matchRating && matchVerified && matchSearch;
  });

  const newCount = reviews.filter((r) => r.isNew).length;
  const avgRating = reviews.length
    ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10
    : 0;
  const verifiedCount = reviews.filter((r) => r.isVerified).length;
  const repliedCount = reviews.filter((r) => r.adminReply).length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <i className="ri-loader-4-line text-3xl animate-spin mb-3"></i>
        <p className="text-sm">جاري تحميل التقييمات...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* New Reviews Alert */}
      {newCount > 0 && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-400 rounded-xl flex-shrink-0 animate-pulse">
              <i className="ri-star-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-amber-800 text-sm font-black">{newCount} تقييم جديد منذ آخر زيارة</p>
              <p className="text-amber-600 text-xs mt-0.5">تقييمات جديدة من عملاء بعد استلام طلباتهم</p>
            </div>
          </div>
          <button
            onClick={handleMarkSeen}
            className="flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-xs font-black px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-double-line text-sm"></i>
            تحديد كمقروء
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "إجمالي التقييمات", value: reviews.length,                                    icon: "ri-star-line",           color: "text-amber-600",  bg: "bg-amber-50"  },
          { label: "متوسط التقييم",    value: avgRating > 0 ? `${avgRating} / 5` : "—",          icon: "ri-star-half-line",      color: "text-green-600",  bg: "bg-green-50"  },
          { label: "مشترين موثّقين",   value: verifiedCount,                                      icon: "ri-shield-check-line",   color: "text-teal-600",   bg: "bg-teal-50"   },
          { label: "ردود الإدارة",     value: repliedCount,                                       icon: "ri-reply-line",          color: "text-indigo-600", bg: "bg-indigo-50" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className={`w-8 h-8 flex items-center justify-center ${stat.bg} rounded-lg mb-3`}>
              <i className={`${stat.icon} ${stat.color} text-sm`}></i>
            </div>
            <p className="text-lg font-black text-[#1a1a1a]">{stat.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Rating distribution */}
      {reviews.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-bold text-gray-400 mb-3">توزيع التقييمات</p>
          <div className="flex flex-col gap-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = reviews.filter((r) => r.rating === star).length;
              const pct = reviews.length > 0 ? Math.round((count / reviews.length) * 100) : 0;
              return (
                <button
                  key={star}
                  onClick={() => setFilterRating(filterRating === star ? "all" : star)}
                  className={`flex items-center gap-3 cursor-pointer rounded-lg px-2 py-1 transition-colors ${filterRating === star ? "bg-amber-50" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-1 w-20 flex-shrink-0">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-xs font-bold text-gray-600">{star}</span>
                    <span className="text-[10px] text-gray-400 mr-1">({count})</span>
                  </div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-left flex-shrink-0">{pct}%</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالاسم، الجوال، المنتج، التعليق..."
            className="w-full border border-gray-200 rounded-xl pr-9 pl-4 py-2.5 text-sm outline-none focus:border-gray-400 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={String(filterRating)}
            onChange={(e) => setFilterRating(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-gray-400 cursor-pointer bg-white"
          >
            <option value="all">كل التقييمات</option>
            {[5, 4, 3, 2, 1].map((s) => (
              <option key={s} value={s}>⭐ {s} نجوم</option>
            ))}
          </select>
          <select
            value={filterVerified}
            onChange={(e) => setFilterVerified(e.target.value as typeof filterVerified)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-gray-400 cursor-pointer bg-white"
          >
            <option value="all">الكل</option>
            <option value="verified">موثّق فقط</option>
            <option value="unverified">غير موثّق</option>
          </select>
          <button
            onClick={fetchReviews}
            className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex-shrink-0"
            title="تحديث"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Reviews list */}
      {reviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
            <i className="ri-star-line text-3xl"></i>
          </div>
          <p className="text-sm font-medium text-gray-500">لا توجد تقييمات بعد</p>
          <p className="text-xs text-gray-400 mt-1">ستظهر تقييمات العملاء هنا بعد استلام طلباتهم</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">لا توجد نتائج تطابق البحث</div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((review) => {
            const ratingInfo = RATING_LABELS[review.rating] ?? RATING_LABELS[5];
            const isDeleting = deleting === review.id;
            const isSavingReply = savingReply === review.id;
            const isReplyOpen = replyOpen === review.id;
            const draftText = replyDrafts[review.id] ?? review.adminReply ?? "";

            return (
              <div
                key={review.id}
                className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${
                  review.isNew ? "border-amber-300 ring-1 ring-amber-200" : "border-gray-100"
                }`}
              >
                {/* New badge bar */}
                {review.isNew && (
                  <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-4 py-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0"></div>
                    <p className="text-amber-700 text-xs font-bold">تقييم جديد</p>
                    <span className="mr-auto text-amber-500 text-[10px]">
                      {new Date(review.createdAt).toLocaleDateString("ar-SA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                )}

                <div className="p-4">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0 font-black text-gray-500 text-sm">
                        {review.reviewerName.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[#1a1a1a] text-sm font-black">{review.reviewerName}</p>
                          {review.isVerified && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
                              <i className="ri-shield-check-line text-xs"></i>
                              مشتري موثّق
                            </span>
                          )}
                          {review.adminReply && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full">
                              <i className="ri-reply-line text-xs"></i>
                              تم الرد
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs mt-0.5">{review.reviewerPhone}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${ratingInfo.color}`}>
                        {ratingInfo.label}
                      </span>
                      <button
                        onClick={() => handleDelete(review.id, review.reviewerName)}
                        disabled={isDeleting}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 text-red-500 transition-colors cursor-pointer disabled:opacity-50"
                        title="حذف التقييم"
                      >
                        {isDeleting ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-delete-bin-line text-sm"></i>}
                      </button>
                    </div>
                  </div>

                  {/* Product + Stars */}
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-100">
                      <i className="ri-box-3-line text-gray-400 text-xs"></i>
                      <p className="text-gray-600 text-xs font-bold truncate max-w-[200px]">{review.productName}</p>
                    </div>
                    <StarDisplay rating={review.rating} />
                  </div>

                  {/* Review text */}
                  <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100 mb-3">
                    <p className="text-gray-700 text-sm leading-relaxed">{review.reviewText}</p>
                  </div>

                  {/* Existing reply display */}
                  {review.adminReply && !isReplyOpen && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 mb-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center bg-indigo-600 rounded-full flex-shrink-0">
                            <i className="ri-shield-user-fill text-white text-[10px]"></i>
                          </div>
                          <p className="text-indigo-700 text-xs font-black">رد الإدارة</p>
                          {review.adminReplyAt && (
                            <span className="text-indigo-400 text-[10px]">
                              · {new Date(review.adminReplyAt).toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setReplyDrafts((prev) => ({ ...prev, [review.id]: review.adminReply ?? "" }));
                              setReplyOpen(review.id);
                            }}
                            className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer px-2 py-1 rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap"
                          >
                            <i className="ri-edit-line text-xs"></i>
                            تعديل
                          </button>
                          <button
                            onClick={() => handleDeleteReply(review.id)}
                            disabled={isSavingReply}
                            className="flex items-center gap-1 text-[10px] font-bold text-red-500 hover:text-red-700 cursor-pointer px-2 py-1 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap disabled:opacity-50"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                            حذف
                          </button>
                        </div>
                      </div>
                      <p className="text-indigo-800 text-sm leading-relaxed">{review.adminReply}</p>
                    </div>
                  )}

                  {/* Reply input box */}
                  {isReplyOpen ? (
                    <div className="border border-indigo-200 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2 bg-indigo-50 px-3 py-2 border-b border-indigo-200">
                        <div className="w-5 h-5 flex items-center justify-center bg-indigo-600 rounded-full flex-shrink-0">
                          <i className="ri-shield-user-fill text-white text-[10px]"></i>
                        </div>
                        <p className="text-indigo-700 text-xs font-black flex-1">
                          {review.adminReply ? "تعديل رد الإدارة" : "رد الإدارة"}
                        </p>
                        <button
                          onClick={() => setReplyOpen(null)}
                          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-indigo-100 text-indigo-400 hover:text-indigo-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                      <div className="p-3">
                        <textarea
                          value={draftText}
                          onChange={(e) =>
                            setReplyDrafts((prev) => ({ ...prev, [review.id]: e.target.value }))
                          }
                          placeholder="اكتب ردك على هذا التقييم... سيظهر للعملاء في صفحة المنتج"
                          rows={3}
                          maxLength={500}
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 resize-none outline-none focus:border-indigo-400 transition-colors"
                        />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-gray-400">{draftText.length}/500</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setReplyOpen(null)}
                              className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              إلغاء
                            </button>
                            <button
                              onClick={() => handleSaveReply(review.id)}
                              disabled={isSavingReply || !draftText.trim()}
                              className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-black rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              {isSavingReply ? (
                                <><i className="ri-loader-4-line animate-spin text-xs"></i> حفظ...</>
                              ) : (
                                <><i className="ri-save-line text-xs"></i> حفظ الرد</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Reply button — only show if no reply yet or reply is hidden */
                    !review.adminReply && (
                      <button
                        onClick={() => {
                          setReplyDrafts((prev) => ({ ...prev, [review.id]: "" }));
                          setReplyOpen(review.id);
                        }}
                        className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-reply-line text-sm"></i>
                        رد على هذا التقييم
                      </button>
                    )
                  )}

                  {/* Footer date */}
                  {!review.isNew && (
                    <p className="text-gray-400 text-[10px] mt-3 text-left">
                      {new Date(review.createdAt).toLocaleDateString("ar-SA", {
                        year: "numeric", month: "long", day: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
